// dashboard/src/services/apiPlaygroundService.ts

export interface ApiRequest {
  method: string;
  url: string;
  headers: Record<string, string>;
  body?: string;
}

export interface ApiResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  data: any;
  responseTime: number;
}

export interface RequestTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  method: string;
  endpoint: string;
  headers: Record<string, string>;
  body?: any;
  example_response?: any;
}

// Get the API base URL from environment or default
const getApiBaseUrl = (): string => {
  return process.env.REACT_APP_API_BASE_URL || 'https://api.crawlycarl.com/api/v1';
};

// Execute an API request
export const executeApiRequest = async (
  request: ApiRequest,
  apiKey: string
): Promise<ApiResponse> => {
  const startTime = Date.now();
  
  try {
    const finalHeaders: Record<string, string> = { ...request.headers };
    finalHeaders['Authorization'] = `Bearer ${apiKey}`;

    // Only set Content-Type if there is a body and it's not already set.
    // This is important for requests like GET or if Content-Type is 'multipart/form-data' etc.
    if (request.body && typeof request.body === 'string' && !finalHeaders['Content-Type']) {
      finalHeaders['Content-Type'] = 'application/json';
    }
    // Remove X-API-Key if it was somehow included in original request.headers
    delete finalHeaders['X-API-Key'];


    const response = await fetch(request.url, {
      method: request.method,
      headers: finalHeaders,
      body: request.body,
    });

    const responseHeaders: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });

    let data;
    try {
      data = await response.json();
    } catch {
      data = await response.text();
    }

    const responseTime = Date.now() - startTime;

    return {
      status: response.status,
      statusText: response.statusText,
      headers: responseHeaders,
      data,
      responseTime,
    };
  } catch (error) {
    const responseTime = Date.now() - startTime;
    const errorResponse: ApiResponse = {
      status: 0,
      statusText: 'Network Error',
      headers: {},
      data: { error: error instanceof Error ? error.message : 'Unknown error' },
      responseTime,
    };
    throw errorResponse;
  }
};

// Get OpenAPI specification
export const getOpenApiSpec = async (): Promise<any> => {
  try {
    const response = await fetch(`${getApiBaseUrl().replace('/api/v1', '')}/openapi.json`);
    if (!response.ok) {
      throw new Error('Failed to fetch OpenAPI specification');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching OpenAPI spec:', error);
    return null;
  }
};

// Generate code snippets for different languages
export const generateCodeSnippet = (
  request: ApiRequest,
  apiKey: string,
  language: 'curl' | 'python' | 'javascript' | 'nodejs'
): string => {
  const { method, url, headers, body } = request;
  
  switch (language) {
    case 'curl':
      let curlCommand = `curl -X ${method.toUpperCase()} "${url}"`;
      curlCommand += ` \\\n  -H "Authorization: Bearer ${apiKey}"`;
      
      Object.entries(headers).forEach(([key, value]) => {
        // Ensure not to duplicate Content-Type if it's already in headers for curl
        // Also, Authorization is now handled above.
        if (key.toLowerCase() !== 'authorization' && key.toLowerCase() !== 'x-api-key') {
          curlCommand += ` \\\n  -H "${key}: ${value}"`;
        }
      });
      
      if (body) {
        curlCommand += ` \\\n  -d '${body}'`;
      }
      
      return curlCommand;

    case 'python':
      let pythonCode = 'import requests\n\n';
      pythonCode += `url = "${url}"\n`;
      pythonCode += `headers = {\n`;
      pythonCode += `    "Authorization": "Bearer ${apiKey}",\n`;
      
      Object.entries(headers).forEach(([key, value]) => {
        if (key.toLowerCase() !== 'authorization' && key.toLowerCase() !== 'x-api-key') {
          pythonCode += `    "${key}": "${value}",\n`;
        }
      });
      
      pythonCode += `}\n\n`;
      
      if (body) {
        pythonCode += `data = ${body}\n\n`;
        pythonCode += `response = requests.${method.toLowerCase()}(url, headers=headers, json=data)\n`;
      } else {
        pythonCode += `response = requests.${method.toLowerCase()}(url, headers=headers)\n`;
      }
      
      pythonCode += `print(response.json())`;
      return pythonCode;

    case 'javascript':
      let jsCode = `const response = await fetch("${url}", {\n`;
      jsCode += `  method: "${method.toUpperCase()}",\n`;
      jsCode += `  headers: {\n`;
      jsCode += `    "Authorization": "Bearer ${apiKey}",\n`;
      
      Object.entries(headers).forEach(([key, value]) => {
        if (key.toLowerCase() !== 'authorization' && key.toLowerCase() !== 'x-api-key') {
          jsCode += `    "${key}": "${value}",\n`;
        }
      });
      
      jsCode += `  },\n`;
      
      if (body) {
        jsCode += `  body: JSON.stringify(${body})\n`;
      }
      
      jsCode += `});\n\n`;
      jsCode += `const data = await response.json();\n`;
      jsCode += `console.log(data);`;
      return jsCode;

    case 'nodejs':
      let nodeCode = 'const axios = require("axios");\n\n';
      nodeCode += `const config = {\n`;
      nodeCode += `  method: "${method.toLowerCase()}",\n`;
      nodeCode += `  url: "${url}",\n`;
      nodeCode += `  headers: {\n`;
      nodeCode += `    "Authorization": "Bearer ${apiKey}",\n`;
      
      Object.entries(headers).forEach(([key, value]) => {
        if (key.toLowerCase() !== 'authorization' && key.toLowerCase() !== 'x-api-key') {
          nodeCode += `    "${key}": "${value}",\n`;
        }
      });
      
      nodeCode += `  },\n`;
      
      if (body) {
        nodeCode += `  data: ${body}\n`;
      }
      
      nodeCode += `};\n\n`;
      nodeCode += `axios(config)\n`;
      nodeCode += `  .then(response => console.log(response.data))\n`;
      nodeCode += `  .catch(error => console.error(error));`;
      return nodeCode;

    default:
      return '';
  }
};

// Get request templates
export const getRequestTemplates = (): RequestTemplate[] => {
  const baseUrl = getApiBaseUrl();

  // Load templates from external JSON file
  try {
    // Dynamic import to avoid TypeScript strict typing issues with JSON
    const templatesData = require('../data/api-templates.json');

    return templatesData.templates.map((template: any): RequestTemplate => ({
      id: template.id,
      name: template.name,
      description: template.description,
      category: template.category,
      method: template.method,
      endpoint: baseUrl + template.endpoint,
      headers: template.headers || {},
      body: template.body,
      example_response: template.example_response
    }));
  } catch (error) {
    console.error('Failed to load templates from JSON file:', error);
    // Fallback to empty array if JSON loading fails
    return [];
  }
};

// Save request to history (localStorage for now)
// Added apiKeyId for potential reference in history UI, not used for execution here
export const saveRequestToHistory = (request: ApiRequest, response: ApiResponse, apiKeyId?: string): void => {
  const history = getRequestHistory();
  const newEntry = {
    id: Date.now().toString(),
    apiKeyId, // Store the selected API Key ID for reference if needed
    timestamp: new Date().toISOString(),
    request,
    response,
  };
  
  const updatedHistory = [newEntry, ...history.slice(0, 49)]; // Keep last 50 requests
  localStorage.setItem('api_playground_history', JSON.stringify(updatedHistory));
};

// Get request history
export const getRequestHistory = (): Array<{
  id: string;
  timestamp: string;
  request: ApiRequest;
  response: ApiResponse;
  apiKeyId?: string; // Optional: if you want to display which key ID was selected at the time
}> => {
  try {
    const history = localStorage.getItem('api_playground_history');
    return history ? JSON.parse(history) : [];
  } catch {
    return [];
  }
};

// Clear request history
export const clearRequestHistory = (): void => {
  localStorage.removeItem('api_playground_history');
};
